import React from 'react';
import { generatePdf, initPdfCtx } from '../utils/pdf-generator';
import { DEFAULT_FONT, FONTS_LIST } from '../components/Pdf/constants';
import { getPdfScheduleSettings } from '../../../api/player/pdfSchedule';
import { FORMAT_A4, FORMAT_FULLPAGE } from '../../../cp/components/EventPage/SchedulePdfConstructorPage/constants';

const PADDING_BOTTOM = 30;

export function usePdf(opt = {}) {
    const targetRef = React.useRef(null);
    const [config, setConfig] = React.useState(opt);
    const [isConfigLoaded, setIsConfigLoaded] = React.useState(false);
    let pdfInstance = null;

    async function _handleGetSettings() {
        try {
            const response = await getPdfScheduleSettings(opt.eventKey);

            setConfig((prev) => {
                return Object.assign({...prev, ...response}, {
                    font: FONTS_LIST.find((font) => font.name === response.font) ?? null,
                    orientation: response.orientation,
                });
            });
            setIsConfigLoaded(true);
        } catch (error) {
            console.error(error);
        }
    }

    async function _initPdf() {
        return new Promise((resolve) => {
            if (isConfigLoaded && targetRef.current) {
                setTimeout(async () => {
                    const formatSize = [targetRef.current.clientWidth, targetRef.current.clientWidth * 1.291666666666667];
        
                    pdfInstance = initPdfCtx(
                        config?.locale,
                        {
                            ...config,
                            format: formatSize,
                        }
                    );
    
                    if (config.format === FORMAT_FULLPAGE) {
                        const {internal} = await pdfInstance;
    
                        internal.pageSize.setWidth(targetRef.current.clientWidth);
                        internal.pageSize.setHeight(targetRef.current.clientHeight + (config.view === 'lines' ? PADDING_BOTTOM : 0));
                    }

                    resolve(pdfInstance);
                }, 0);
            }
        });
    }

    async function _waitForImagesToLoad(container) {
        const images = container.querySelectorAll('img');

        if (!images.length) {
            return;
        }

        const promises = Array.from(images).map((image) => {
            if (image.complete && image.naturalHeight !== 0) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                image.onload = () => resolve();
                image.onerror = () => resolve();
            });
        });

        return Promise.all(promises);
    }

    async function _createPdf() {
        if (!targetRef.current) {
            return console.error('Node of html is undefined');
        }

        await _waitForImagesToLoad(targetRef.current);

        await _initPdf();
        
        const options = {
            fileName: config?.fileName ?? 'document',
            font: !!config.font ? config.font : DEFAULT_FONT,
            ...config,
            pdfOptions: {
                ...config.pdfOptions,
                callback: function (pdf) {
                    pdfInstance = pdf;
                },
            },
        };

        await generatePdf(targetRef.current, options);

        return pdfInstance;
    };

    async function savePdf() {
        pdfInstance = await _createPdf();
        
        if (!pdfInstance) {
            return console.error('Pdf instance not found');
        }

        pdfInstance.save(`${config.fileName || 'document'}.pdf`);
    };

    React.useEffect(() => {
        _handleGetSettings();
    }, []);

    return {
        target: targetRef,
        pdfSettings: config,
        setConfig,
        savePdf,
    };
};
